import { RagIndex } from '@/core/indexesEnum';
import {
  DayOfWeek,
  FrequencyType,
  PeriodSelector
} from '@/models/period-selector';
import { StorageKeys } from '@/models/storage-keys';
import type { UserSettings } from '@/models/user-settings';
import { SettingsUtils } from '@/utils/settings.utils';
import { StorageUtils } from '@/utils/storage.utils';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useUserSettingsStore = defineStore('userSettings', () => {
  const initialized = ref(false);
  const authorizationCountry = ref<string>('');
  const source = ref<string>('');
  const userFolderUrl = ref<string>('');
  const anonymization = ref<boolean>(false);
  const automaticIndexing = ref<boolean>(false);
  const indexingSettings = ref<PeriodSelector>({
    frequency: FrequencyType.Daily,
    hour: 0,
    dayOfWeek: undefined
  });
  const gptConsumption = ref<{
    inputTokens: number;
    completionTokens: number;
    indexSize: number;
  }>({
    inputTokens: 0,
    completionTokens: 0,
    indexSize: 0
  });
  const selectedRagIndex = ref<RagIndex>();

  async function initializeData() {
    if (initialized.value) return;

    const storedSettings = await StorageUtils.getStorageItem(
      StorageKeys.USER_SETTINGS
    );
    if (storedSettings) {
      const settings = SettingsUtils.convertFromJsonString(storedSettings);

      authorizationCountry.value = settings.authorizationSelectedCountry || '';
      source.value = settings.source || '';
      userFolderUrl.value = settings.userFolderUrl || '';
      anonymization.value = settings.anonimization ?? false;
      automaticIndexing.value = settings.automaticIndexing ?? false;

      indexingSettings.value = {
        frequency: settings.indexingSettings?.frequency ?? FrequencyType.Daily,
        hour: settings.indexingSettings?.hour ?? 0,
        dayOfWeek: settings.indexingSettings?.dayOfWeek
      };

      gptConsumption.value = {
        inputTokens: settings.gptConsumption?.inputTokens ?? 0,
        completionTokens: settings.gptConsumption?.completionTokens ?? 0,
        indexSize: settings.gptConsumption?.indexSize ?? 0
      };

      selectedRagIndex.value = settings.selectedIndex;
    }

    initialized.value = true;
  }

  async function persist() {
    const settings: UserSettings = {
      authorizationSelectedCountry: authorizationCountry.value,
      source: source.value,
      userFolderUrl: userFolderUrl.value,
      anonimization: anonymization.value,
      automaticIndexing: automaticIndexing.value,
      indexingSettings: indexingSettings.value,
      gptConsumption: gptConsumption.value,
      selectedIndex: selectedRagIndex.value
    };
    await StorageUtils.setStorageItem(StorageKeys.USER_SETTINGS, settings);
  }

  function setAuthorizationCountryCode(code: string) {
    authorizationCountry.value = code;
    persist();
  }

  function setSource(newSource: string) {
    source.value = newSource;
    persist();
  }

  function setUserFolder(
    url: string,
    consumption: {
      inputTokens: number;
      completionTokens: number;
      indexSize: number;
    }
  ) {
    userFolderUrl.value = url;
    gptConsumption.value = consumption;
    persist();
  }

  function setAnonymization(value: boolean) {
    anonymization.value = value;
    persist();
  }

  function setAutomaticIndexing(value: boolean) {
    automaticIndexing.value = value;
    if (!value) {
      indexingSettings.value = {
        frequency: FrequencyType.Daily,
        hour: 0,
        dayOfWeek: undefined
      };
    }
    persist();
  }

  function setIndexingSettings(settings: PeriodSelector) {
    if (settings.frequency === FrequencyType.Monthly) {
      indexingSettings.value = {
        frequency: settings.frequency,
        hour: undefined,
        dayOfWeek: undefined
      };
    } else if (settings.frequency === FrequencyType.Weekly) {
      indexingSettings.value = {
        frequency: settings.frequency,
        hour: settings.hour ?? 0,
        dayOfWeek: settings.dayOfWeek ?? DayOfWeek.Monday
      };
    } else {
      indexingSettings.value = {
        frequency: settings.frequency,
        hour: settings.hour ?? 0,
        dayOfWeek: undefined
      };
    }
    persist();
  }

  function setSelectedRagIndex(index: RagIndex) {
    selectedRagIndex.value = index;
    persist();
  }

  function getSafeSelectedRagIndex(): RagIndex {
    return selectedRagIndex.value ?? RagIndex.NAW;
  }

  return {
    initializeData,
    persist,
    authorizationCountry,
    source,
    userFolderUrl,
    anonymization,
    automaticIndexing,
    indexingSettings,
    gptConsumption,
    selectedRagIndex,
    getSafeSelectedRagIndex,
    setAuthorizationCountryCode,
    setSource,
    setUserFolder,
    setAnonymization,
    setAutomaticIndexing,
    setIndexingSettings,
    setSelectedRagIndex
  };
});
